/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import DefaultApiLimit from '../../config/defaultLimit';
import { filterType } from '../../config/enum';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  AvailableMaintenance,
  FetchMaintenanceProperties,
  FilterScheduleInterFace,
  RequestScheduleInterFace,
  ScheduleInitialProperties,
} from '../../interface/partners-maintenance-scheduling.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  RestFilterAndAddSingleFilter,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import { removeEmptyKeys } from '../../shared-functions/DataTime';
import FormatUtcString from '../../shared-functions/FormatUtcString';
import maintenanceScheduleApi from '../../utils/api/v2/maintenance-schedule';

// a thunk to fetch devices
export const fetchAllMaintenanceSchedules = createAsyncThunk<
  FetchMaintenanceProperties,
  RequestScheduleInterFace
>(
  'maintenance-schedule-management/fetchAllMaintenanceSchedules',
  async (data: RequestScheduleInterFace, { dispatch }) => {
    try {
      const response = await maintenanceScheduleApi.fetchMaintenanceSchedule(removeEmptyKeys(data));
      return {
        ...response.data,
        results: response.data.results.map((deviceData: AvailableMaintenance) => ({
          ...deviceData,
          date: ConvertUTCtoDate(deviceData?.startDateUtc),
          time: FormatUtcString(ConvertUTCtoHHMM(deviceData?.startDateUtc)),
          startDate: ConvertUTCtoDate(deviceData?.startDateUtc),
          startTime: FormatUtcString(ConvertUTCtoHHMM(deviceData?.startDateUtc)),
          endDate: ConvertUTCtoDate(deviceData?.endDateUtc),
          endTime: FormatUtcString(ConvertUTCtoHHMM(deviceData?.endDateUtc)),
          name: deviceData?.chargingStationName,
          address: deviceData?.chargingStationAddress,
          chargingStationId: deviceData?.chargingStation || deviceData?.chargingStationData?._id,
          duration: deviceData?.duration || '-',
          id: deviceData?._id,
        })),
      };
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

// a thunk to refresh maintenance schedules
export const refreshAllMaintenanceSchedules = createAsyncThunk<
  FetchMaintenanceProperties,
  RequestScheduleInterFace
>(
  'maintenance-schedule-management/refreshAllMaintenanceSchedules',
  async (data: RequestScheduleInterFace) => {
    const response = await maintenanceScheduleApi.fetchMaintenanceSchedule(removeEmptyKeys(data));
    return {
      ...response.data,
      results: response.data.results.map((deviceData: AvailableMaintenance) => ({
        ...deviceData,
        date: ConvertUTCtoDate(deviceData?.startDateUtc),
        time: FormatUtcString(ConvertUTCtoHHMM(deviceData?.startDateUtc)),
        startDate: ConvertUTCtoDate(deviceData?.startDateUtc),
        startTime: FormatUtcString(ConvertUTCtoHHMM(deviceData?.startDateUtc)),
        endDate: ConvertUTCtoDate(deviceData?.endDateUtc),
        endTime: FormatUtcString(ConvertUTCtoHHMM(deviceData?.endDateUtc)),
        name: deviceData?.chargingStationName,
        address: deviceData?.chargingStationAddress,
        chargingStationId: deviceData?.chargingStation || deviceData?.chargingStationData?._id,
        duration: deviceData?.duration || '-',
        id: deviceData?._id,
      })),
    };
  },
);

export const fetchMaintenanceManagementListFilters = createAsyncThunk<
  CommonFilterProperties[],
  void
>(
  'maintenance-schedule-management/fetchMaintenanceManagementListFilters',
  async (_, { dispatch }) => {
    try {
      const response = await maintenanceScheduleApi.fetchListFilters();
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(
        response.data,
      ).filter((filter) => filter.type !== filterType.DATE_RANGE);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

const stationFilter: CommonFilterProperties[] = [
  {
    name: 'searchPlaceholder',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: ScheduleInitialProperties = {
  schedules: [],
  loading: false,
  page: DefaultApiLimit.page,
  limit: DefaultApiLimit.limit,
  totalPages: 1,
  totalResults: 0,
  allMaintenanceSchedule: [],
  filter: {},
  filters: stationFilter,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: null,
  sortField: null,
};

// Create the slice
export const maintenanceScheduleSlice = createSlice({
  name: 'maintenance-schedule-management',
  initialState,
  reducers: {
    /**
     * Change the device point status function
     * @param {any} state - The current state
     * @param {any} action - The filter payload
     */
    setScheduleFilterData: (
      state: ScheduleInitialProperties,
      action: PayloadAction<FilterScheduleInterFace>,
    ) => {
      state.filter = action.payload;
    },
    /**
     * Function that reset slice to initial state
     * @param {any} state - The current state
     */
    resetMaintenanceToInitialState: (state) => {
      Object.assign(state, initialState);
    },

    /**
     * Update station management filters
     * @param {any} state - The current state
     * @param {any} action - The filter update payload
     */
    updateMaintenanceManagementFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
        state.sortBy = null;
        state.sortField = null;
      } else if (payload.type === 'restAndAdd') {
        const { updatedCommonFilter, isFilterUpdated } = RestFilterAndAddSingleFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param {any} state - The current state
     */
    disableMaintenanceManagementFiltersApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * function for set sortBy
     * @param {any} state - The current state
     * @param {any} action - The sort data payload
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortBy = action.payload.sortBy;
      state.sortField = action.payload.sortField;
    },
  },

  /**
   * Extra reducers to handle thunk request
   * @param {any} builder - The slice builder
   */
  extraReducers: (builder: ActionReducerMapBuilder<ScheduleInitialProperties>) => {
    builder
      .addCase(fetchAllMaintenanceSchedules.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchAllMaintenanceSchedules.fulfilled,
        (state, action: PayloadAction<FetchMaintenanceProperties>) => {
          state.loading = false;
          state.page = action.payload.page;
          state.limit = action.payload.limit;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
          state.schedules = action.payload.results;
        },
      )
      .addCase(fetchAllMaintenanceSchedules.rejected, (state) => {
        state.loading = false;
      });
    builder.addCase(refreshAllMaintenanceSchedules.fulfilled, (state, action) => {
      state.page = action.payload.page;
      state.limit = action.payload.limit;
      state.totalPages = action.payload.totalPages;
      state.totalResults = action.payload.totalResults;
      state.schedules = action.payload.results;
    });
    builder
      .addCase(fetchMaintenanceManagementListFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchMaintenanceManagementListFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload].filter(
            (object, index, self) => index === self.findIndex((t) => t.key === object.key) 
            && object.type !== filterType.DATE_RANGE
          )
        },
      )
      .addCase(fetchMaintenanceManagementListFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export the actions
export const {
  setScheduleFilterData,
  resetMaintenanceToInitialState,
  updateMaintenanceManagementFilters,
  disableMaintenanceManagementFiltersApplied,
  setSortData,
} = maintenanceScheduleSlice.actions;

// Export the reducer
export default maintenanceScheduleSlice.reducer;
